import { BaseCategory } from "./BaseCategory";
import { AuthoringBlockType, HeaderPositionType, TextStyleType } from "../constants";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class VennDiagramCategory extends BaseCategory {
    get name() {
        return "Venn Diagram";
    }

    get type() {
        return SlideCategories.VENN_DIAGRAM;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content represents overlapping sets, relationships, or commonalities between concepts. Use this for showing shared characteristics, intersections between groups, or comparing and contrasting multiple items (typically 2-4 circles).";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a Venn diagram with overlapping circles to show relationships and commonalities.
        Provide 2-4 circles with labels and optional descriptions.
        Each circle represents a distinct concept or category.`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            circles: [{
                label: "main label for this circle (heading text)",
                description: "optional: additional description or details for this circle",
                size: "optional: relative size of the circle (50-250, default: 150)"
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    getSlideModels(model) {
        let models = [
            this.buildVennDiagram(model)
        ];
        return models;
    }

    buildVennDiagram(model) {
        let builder = new SlideModelBuilder("vennDiagram", model);

        // Add header if there's a title
        if (model.slideTitle) {
            builder.createHeader({
                heading: model.slideTitle,
                position: HeaderPositionType.TOP
            });
        }

        // Determine number of circles (between 2 and 4)
        const circleCount = Math.min(Math.max(model.circles?.length || 3, 2), 4);

        // Ensure we have circles data
        let circles = model.circles || [];

        // If we don't have enough circles, generate placeholder data
        while (circles.length < circleCount) {
            circles.push({
                label: `Circle ${circles.length + 1}`,
                size: 150
            });
        }

        // Limit to 4 circles
        circles = circles.slice(0, circleCount);

        // Create circle items with preset positions
        const positions = this.getCirclePositions(circleCount);
        const items = circles.map((circle, index) => {
            const position = positions[index];
            const blocks = [
                {
                    html: circle.label || `Circle ${index + 1}`,
                    textStyle: TextStyleType.HEADING,
                    type: AuthoringBlockType.TEXT,
                }
            ];

            // Add description if provided
            // if (circle.description) {
            //     blocks.push({
            //         html: circle.description,
            //         textStyle: TextStyleType.BODY,
            //         type: AuthoringBlockType.TEXT,
            //     });
            // }

            return {
                x: position.x,
                y: position.y,
                radius: circle.size || 150,
                labelCenterX: 0,
                labelCenterY: 0,
                text: {
                    blocks: blocks
                }
            };
        });

        // Create the primary venn diagram element
        builder.createPrimary({
            items,
            collectionColor: "colorful"
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    getCirclePositions(count) {
        // Preset positions based on number of circles
        // These match the presetLayout positions in VennDiagram.js
        switch (count) {
            case 2:
                return [
                    { x: -150, y: 0 },
                    { x: 150, y: 0 }
                ];
            case 3:
                return [
                    { x: 0, y: -100 },
                    { x: -100, y: 100 },
                    { x: 100, y: 100 }
                ];
            case 4:
                return [
                    { x: -100, y: -100 },
                    { x: 100, y: -100 },
                    { x: -100, y: 100 },
                    { x: 100, y: 100 }
                ];
            default:
                // Fallback for 3 circles
                return [
                    { x: 0, y: -100 },
                    { x: -100, y: 100 },
                    { x: 100, y: 100 }
                ];
        }
    }
}
